import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { US_STATES } from "@/lib/us-states";
import { ZodError } from "zod";
import { siteSchema, type SiteValues } from "./schemas";

interface Props {
  values: SiteValues;
  onChange: (next: SiteValues) => void;
  errors: Partial<Record<keyof SiteValues, string>>;
}

export default function StepFirstSite({ values, onChange, errors }: Props) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Optional — add the first property you'll be working on. We'll spin up
        a draft Phase&nbsp;I ESA report for it so you can pick up where the
        wizard ends.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="setup-address">Property address</Label>
        <AddressAutocomplete
          id="setup-address"
          value={values.address ?? ""}
          onChange={(v) => onChange({ ...values, address: v })}
          onPlaceSelected={(parsed) =>
            onChange({
              ...values,
              address: parsed.street,
              city: parsed.city,
              state: parsed.state,
              zip: parsed.zip,
              lat: parsed.lat,
              lng: parsed.lng,
            })
          }
          placeholder="Start typing a US address…"
        />
        {errors.address && (
          <p className="text-xs text-destructive">{errors.address}</p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="setup-city">City</Label>
          <Input
            id="setup-city"
            value={values.city ?? ""}
            onChange={(e) => onChange({ ...values, city: e.target.value })}
            maxLength={80}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="setup-state">State</Label>
          <Select
            value={values.state || ""}
            onValueChange={(v) => onChange({ ...values, state: v })}
          >
            <SelectTrigger id="setup-state">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {US_STATES.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5 max-w-[200px]">
        <Label htmlFor="setup-zip">ZIP</Label>
        <Input
          id="setup-zip"
          value={values.zip ?? ""}
          onChange={(e) => onChange({ ...values, zip: e.target.value })}
          maxLength={10}
          placeholder="97204"
          aria-invalid={!!errors.zip}
        />
        {errors.zip && <p className="text-xs text-destructive">{errors.zip}</p>}
      </div>
    </div>
  );
}

export function validateSite(v: SiteValues) {
  try {
    siteSchema.parse(v);
    return { ok: true as const, errors: {} };
  } catch (e) {
    if (e instanceof ZodError) {
      const errors: Partial<Record<keyof SiteValues, string>> = {};
      for (const issue of e.issues) {
        const k = issue.path[0] as keyof SiteValues;
        if (!errors[k]) errors[k] = issue.message;
      }
      return { ok: false as const, errors };
    }
    return { ok: false as const, errors: {} };
  }
}
